import { lerPagina } from '@titularis/ui';
import type { EstadoDoItem, MotorDoComentario } from '@titularis/api-client';
import { api } from '../../api';
import { ESTADO_DA_LISTA_LABEL } from '../../dominio';

/**
 * A consulta que define a lista de trabalho: o que filtra, em que ordem e que
 * página.
 *
 * **Mora fora da tela pelo mesmo motivo da lista de Questões:** o recorte é lido
 * pela lista e pelo resumo, e duas leituras divergiriam no primeiro filtro novo.
 * A divergência apareceria como a contagem do `Segmented` prometendo itens que a
 * tabela não mostra - o pior tipo de defeito, porque não levanta erro nenhum.
 */

export const TAMANHOS_DE_PAGINA = [20, 50, 100];

/*
 * Vinte por página porque esta tela é de **triagem**, e não de conferência em
 * lote: quem chega aqui procura o que travou, olha as primeiras linhas e age. A
 * lista de Questões abre com cem pelo motivo contrário - lá se varre a prova
 * inteira.
 */
export const POR_PAGINA_PADRAO = 20;

export const ORDENS = [
  { value: 'recentes', label: 'Entraram por último' },
  /*
   * O avesso, que é o que responde "o que está esperando há mais tempo" - a
   * pergunta que a ordem por recentes nunca responde, porque o esquecido é
   * justamente o que ela empurra para a última página.
   */
  { value: 'antigas', label: 'Esperam há mais tempo' },
];

export type OrdemDaLista = 'antigas';

/** Os estados, na ordem em que o trabalho anda. */
export const ESTADOS: EstadoDoItem[] = [
  'pendente',
  'reivindicada',
  'aguardando_acervo',
  'feita',
  'falhou',
];

export const MOTORES: MotorDoComentario[] = ['claude_code', 'openrouter'];

/**
 * O recorte, sem o estado.
 *
 * Separado porque é exatamente o que o resumo recebe: contar por estado é o que
 * se quer saber **antes** de escolher o estado.
 */
export interface RecorteDaLista {
  examId?: string;
  motor?: MotorDoComentario;
  /** Só o que está com um motor há tempo demais - a régua é do servidor. */
  paradas: boolean;
  termo: string;
}

export interface ConsultaDaLista extends RecorteDaLista {
  status?: EstadoDoItem;
  ordem?: OrdemDaLista;
  page: number;
  porPagina: number;
}

/**
 * Lê da URL a consulta inteira.
 *
 * O que vem de lá é texto que qualquer um pode digitar na barra de endereço,
 * então estado, motor, ordem e tamanho de página passam pela lista de valores
 * conhecidos antes de virar consulta - o servidor recusaria, mas com erro em vez
 * de lista vazia.
 */
export function lerConsulta(params: URLSearchParams): ConsultaDaLista {
  const texto = (chave: string): string | undefined => params.get(chave) ?? undefined;
  const status = texto('status');
  const motor = texto('motor');
  const porPagina = Number(texto('porPagina'));

  return {
    status: ESTADOS.includes(status as EstadoDoItem) ? (status as EstadoDoItem) : undefined,
    examId: texto('examId'),
    motor: MOTORES.includes(motor as MotorDoComentario)
      ? (motor as MotorDoComentario)
      : undefined,
    paradas: texto('paradas') === '1',
    termo: texto('termo') ?? '',
    ordem: texto('ordem') === 'antigas' ? 'antigas' : undefined,
    page: lerPagina(params),
    porPagina: TAMANHOS_DE_PAGINA.includes(porPagina) ? porPagina : POR_PAGINA_PADRAO,
  };
}

/** O que a API recebe, dos dois lados: a lista e o resumo pedem o mesmo recorte. */
const comoFiltro = (recorte: RecorteDaLista) => ({
  examId: recorte.examId,
  engine: recorte.motor,
  paradas: recorte.paradas ? ('1' as const) : undefined,
  termo: recorte.termo || undefined,
});

/** Chave e função de uma página da lista. */
export function paginaDaLista(consulta: ConsultaDaLista) {
  const { status, ordem, page, porPagina, ...recorte } = consulta;

  return {
    queryKey: ['comment-jobs', 'pagina', { ...recorte, status, ordem, page, porPagina }],
    queryFn: () =>
      api.listarComentarios({
        ...comoFiltro(recorte),
        status,
        ordem,
        page,
        pageSize: porPagina,
      }),
  };
}

/**
 * Chave e função do resumo da fila.
 *
 * **Sem o estado, sem a ordem e sem a página**, e é isso que faz o resumo
 * sobreviver a virar a página: a chave não muda, então o cache responde sem ir à
 * rede. Fosse um campo da listagem, cada clique recontaria a fila inteira.
 */
export function resumoDaLista(consulta: ConsultaDaLista) {
  const { status: _status, ordem: _ordem, page: _page, porPagina: _porPagina, ...recorte } = consulta;

  return {
    queryKey: ['comment-jobs', 'resumo', recorte],
    queryFn: () => api.resumoDosComentarios(comoFiltro(recorte)),
  };
}

/** Quantos recortes estão valendo, para o contador ao lado do botão de filtros. */
export function quantosFiltros(recorte: RecorteDaLista): number {
  return (
    (recorte.examId ? 1 : 0) +
    (recorte.motor ? 1 : 0) +
    (recorte.paradas ? 1 : 0) +
    (recorte.termo ? 1 : 0)
  );
}

/** As opções do filtro de estado, com quanto há em cada um. */
export function opcoesDeEstado(
  porEstado: Record<EstadoDoItem, number> | undefined,
): { label: string; value: EstadoDoItem | 'todos'; count?: number }[] {
  const total = porEstado
    ? Object.values(porEstado).reduce((soma, valor) => soma + valor, 0)
    : undefined;

  return [
    { label: 'Todas', value: 'todos', count: total },
    ...ESTADOS.map((estado) => ({
      label: ESTADO_DA_LISTA_LABEL[estado],
      value: estado,
      count: porEstado?.[estado],
    })),
  ];
}
